/*
  Injects theme overrides into the Bazaarvoice reviews widget. BV renders inside an open shadow root
  that it creates after bv.js loads, so watch the widget for shadow roots (including nested ones)
  and copy the section's <template class="bv-reviews__shadow-styles"> into each. Shadow roots stay
  observed so the styles are re-added if BV re-renders its content.
*/
(() => {
  const STYLE_ATTR = 'data-bv-reviews-styles';
  const POLL_INTERVAL = 250;
  const POLL_TIMEOUT = 30000;

  const injectInto = (shadowRoot, template, seen) => {
    if (seen.has(shadowRoot)) return;
    seen.add(shadowRoot);

    const ensureStyles = () => {
      if (!shadowRoot.querySelector(`style[${STYLE_ATTR}]`)) {
        const style = template.content.querySelector('style').cloneNode(true);
        style.setAttribute(STYLE_ATTR, '');
        shadowRoot.appendChild(style);
      }
      scan(shadowRoot, template, seen);
    };

    ensureStyles();
    new MutationObserver(ensureStyles).observe(shadowRoot, { childList: true, subtree: true });
  };

  const scan = (root, template, seen) => {
    if (root.shadowRoot) injectInto(root.shadowRoot, template, seen);
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) injectInto(el.shadowRoot, template, seen);
    });
  };

  const init = (section) => {
    if (section.dataset.bvReviewsInit) return;
    section.dataset.bvReviewsInit = 'true';

    const widget = section.querySelector('[data-bv-show="reviews"]');
    const template = section.querySelector('.bv-reviews__shadow-styles');
    if (!widget || !template) return;

    const seen = new WeakSet();
    scan(widget, template, seen);
    new MutationObserver(() => scan(widget, template, seen)).observe(widget, { childList: true, subtree: true });

    // BV may attach the shadow root to the widget itself (or an existing child) after render, which
    // fires no light-DOM mutation; poll until one shows up.
    const startedAt = Date.now();
    const poll = setInterval(() => {
      scan(widget, template, seen);
      if (seenAny(widget, seen) || Date.now() - startedAt > POLL_TIMEOUT) {
        clearInterval(poll);
      }
    }, POLL_INTERVAL);
  };

  const seenAny = (widget, seen) =>
    (widget.shadowRoot && seen.has(widget.shadowRoot)) ||
    [...widget.querySelectorAll('*')].some((el) => el.shadowRoot && seen.has(el.shadowRoot));

  document.querySelectorAll('.bv-reviews').forEach(init);
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('.bv-reviews').forEach(init));
})();
